import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from "react";

/**
 * Firestore-stored images: { base64?, url? } from CMS / Storage.
 * Use resolveFirestoreImage + pending gating so public/ fallbacks do not flash before CMS data loads.
 */
const PLACEHOLDER_URL = /^(hero|about|project|gallery)_\d/;

export function resolveFirestoreImage(image, fallback = "") {
  if (!image) return fallback;
  if (typeof image === "string") return image || fallback;
  if (image.base64) return image.base64;
  if (image.url && !PLACEHOLDER_URL.test(image.url)) return image.url;
  if (image.src) return image.src;
  return fallback;
}

/** Inline style for background-image; when pending, no image (pair with .cms-hero-pending-fill). */
export function cmsHeroBackgroundStyle(pending, image, fallback) {
  if (pending) return { backgroundImage: "none" };
  const url = resolveFirestoreImage(image, fallback);
  return { backgroundImage: `url('${url}')` };
}

function getReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * After `enabled` and `resolvedUrl` are set, becomes true once the image has loaded (or failed).
 * Use for hero background fade-in. Skips wait when prefers-reduced-motion.
 */
export function useCmsHeroImageReady(resolvedUrl, enabled) {
  const [ready, setReady] = useState(() => (getReducedMotion() && enabled && resolvedUrl ? true : false));

  useEffect(() => {
    if (!enabled || !resolvedUrl) {
      setReady(false);
      return;
    }
    if (getReducedMotion()) {
      setReady(true);
      return;
    }
    setReady(false);
    const img = new Image();
    let cancelled = false;
    const done = () => {
      if (!cancelled) setReady(true);
    };
    img.onload = done;
    img.onerror = done;
    img.src = resolvedUrl;
    return () => {
      cancelled = true;
    };
  }, [resolvedUrl, enabled]);

  return ready;
}

/**
 * @param {boolean} pending — true until Firestore fetch settled (success or error)
 * @param {object|string|null} image — CMS object or string URL
 * @param {string} fallback — static asset path once ready and CMS empty
 */
export function CmsImage({
  pending,
  image,
  fallback,
  alt = "",
  className,
  style,
  onLoad,
  onError,
  loading = "lazy",
  ...rest
}) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);
  const src = pending ? null : resolveFirestoreImage(image, fallback);

  /**
   * Cached / already-decoded images often never fire onLoad after React attaches handlers
   * (especially on client-side route changes). Opacity stayed at 0 → "blank" cards until refresh.
   */
  useLayoutEffect(() => {
    if (pending || !src) {
      setLoaded(false);
      return;
    }
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth > 0) {
      setLoaded(true);
    } else {
      setLoaded(false);
    }
  }, [pending, src]);

  const handleLoad = useCallback(
    (e) => {
      setLoaded(true);
      onLoad?.(e);
    },
    [onLoad]
  );

  const handleError = useCallback(
    (e) => {
      onError?.(e);
      // Avoid stuck invisible img if the request fails (parent may rewrite src in onError).
      setLoaded(true);
    },
    [onError]
  );

  if (pending) {
    return (
      <div
        className={`cms-media-pending ${className || ""}`.trim()}
        style={style}
        aria-hidden
      />
    );
  }

  const motionReduce = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const showLoaded = loaded || motionReduce;

  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      className={`cms-media-img${showLoaded ? " cms-media-img--loaded" : ""} ${className || ""}`.trim()}
      style={style}
      loading={loading}
      decoding="async"
      onLoad={handleLoad}
      onError={handleError}
      {...rest}
    />
  );
}
